<template>
  <section class="candidates-table-shell settings-panel">
    <div class="settings-org-section">
      <SettingsSectionHeader
        title="Lead Sources"
        subtitle="Recruiting source options used on Potential leads"
        icon="pi-share-alt"
      >
        <template #actions>
          <Button
            label="Add Source"
            icon="pi pi-plus"
            size="small"
            :disabled="saving"
            @click="openCreate"
          />
          <Button
            icon="pi pi-refresh"
            label="Refresh"
            size="small"
            severity="secondary"
            outlined
            :loading="loading"
            @click="loadItems"
          />
        </template>
      </SettingsSectionHeader>

      <div class="settings-org-section__body">
        <div v-if="loading && !items.length" class="settings-org-section__state">
          <ProgressSpinner style="width: 2rem; height: 2rem" strokeWidth="4" />
        </div>

        <Message
          v-else-if="error && !items.length"
          severity="error"
          :closable="false"
          class="settings-org-section__message"
        >
          <div class="flex flex-col sm:flex-row sm:items-center gap-3">
            <span class="flex-1">{{ error }}</span>
            <Button label="Retry" size="small" severity="danger" outlined @click="loadItems" />
          </div>
        </Message>

        <template v-else>
          <SettingsOrgReferenceToolbar
            v-model:search-query="searchQuery"
            :count-label="countLabel"
            search-placeholder="Search sources..."
            :loading="loading"
          />

          <div v-if="!filteredItems.length" class="settings-org-section__empty">
            <i class="pi pi-share-alt settings-org-section__empty-icon" aria-hidden="true" />
            <p>{{ searchQuery ? 'No sources match your search.' : 'No lead sources yet.' }}</p>
            <Button
              v-if="searchQuery"
              label="Clear search"
              size="small"
              severity="secondary"
              text
              @click="searchQuery = ''"
            />
          </div>

          <DataTable
            v-else
            :value="filteredItems"
            size="small"
            dataKey="id"
            sort-mode="single"
            removable-sort
            class="candidates-table"
          >
            <Column field="label" header="Label" sortable style="min-width: 12rem">
              <template #body="{ data }">
                <span class="font-medium">{{ data.label }}</span>
              </template>
            </Column>
            <Column field="sortOrder" header="Sort" sortable style="width: 5rem">
              <template #body="{ data }">
                <span class="text-sm font-mono text-surface-600 dark:text-surface-400">
                  {{ data.sortOrder ?? 0 }}
                </span>
              </template>
            </Column>
            <Column field="active" header="Status" sortable style="width: 7rem">
              <template #body="{ data }">
                <Tag
                  :value="data.active ? 'Active' : 'Inactive'"
                  :severity="data.active ? 'success' : 'secondary'"
                  rounded
                />
              </template>
            </Column>
            <Column header="Actions" style="width: 6rem">
              <template #body="{ data }">
                <div class="crc-table-row-actions">
                  <Button
                    icon="pi pi-pencil"
                    size="small"
                    severity="secondary"
                    outlined
                    rounded
                    v-tooltip.top="'Edit'"
                    :disabled="saving"
                    @click="openEdit(data)"
                  />
                </div>
              </template>
            </Column>
          </DataTable>
        </template>
      </div>
    </div>

    <SimpleReferenceEditModal
      ref="modalRef"
      v-model:visible="modalVisible"
      :saving="saving"
      create-title="Add Lead Source"
      edit-title="Edit Lead Source"
      label-placeholder="e.g. Referral"
      icon-class="pi-share-alt"
      @save="onSave"
    />
  </section>
</template>

<script setup>
import { onMounted, ref } from 'vue';
import Button from 'primevue/button';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Tag from 'primevue/tag';
import Message from 'primevue/message';
import ProgressSpinner from 'primevue/progressspinner';
import { useToast } from 'primevue/usetoast';
import SettingsSectionHeader from './SettingsSectionHeader.vue';
import SettingsOrgReferenceToolbar from './SettingsOrgReferenceToolbar.vue';
import SimpleReferenceEditModal from './modals/SimpleReferenceEditModal.vue';
import { useLeadSources } from '../composables/useLeadSources';

defineOptions({ name: 'SettingsLeadSourcesSection' });

const toast = useToast();
const modalRef = ref(null);
const modalVisible = ref(false);

const {
  loading,
  saving,
  error,
  items,
  searchQuery,
  filteredItems,
  countLabel,
  loadItems,
  saveItem
} = useLeadSources();

function openCreate() {
  modalRef.value?.openCreate();
}

function openEdit(item) {
  modalRef.value?.openEdit(item);
}

async function onSave(payload) {
  try {
    await saveItem(payload);
    modalVisible.value = false;
    toast.add({
      severity: 'success',
      summary: 'Saved',
      detail: payload.id ? 'Lead source updated.' : 'Lead source created.',
      life: 2500
    });
  } catch (e) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: e?.message || 'Failed to save lead source.',
      life: 4000
    });
  }
}

onMounted(() => {
  loadItems();
});

defineExpose({ reload: loadItems });
</script>
